import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { X } from 'lucide-react';

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setImages(response.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchImages();
  }, []);

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">Memories</span>
          <h1 className="font-outfit text-4xl md:text-5xl font-black text-white mt-2">
            Photo <span className="text-red-500">Gallery</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl">
            Capturing moments of learning, growth, and achievement at STUDY POINT.
          </p>
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-500 text-lg">No images in gallery yet.</p>
            <p className="text-zinc-600 mt-2">Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, idx) => (
              <div
                key={image.image_id}
                onClick={() => setSelectedImage(image)}
                className={`card-hover cursor-pointer overflow-hidden rounded-sm aspect-square animate-fadeIn stagger-${(idx % 5) + 1}`}
                data-testid={`gallery-image-${image.image_id}`}
              >
                <img
                  src={image.image_url}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-300 hover:scale-110 protected-content"
                  onContextMenu={(e) => e.preventDefault()}
                  draggable={false}
                />
              </div>
            ))}
          </div>
        )}

        {/* Lightbox */}
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-4xl bg-[#0A0A0A] border-zinc-800 p-0">
            {selectedImage && (
              <div className="relative">
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 z-10 w-10 h-10 rounded-sm bg-black/50 flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="protected-content">
                  <img
                    src={selectedImage.image_url}
                    alt={selectedImage.title}
                    className="w-full max-h-[70vh] object-contain"
                    onContextMenu={(e) => e.preventDefault()}
                    draggable={false}
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-outfit text-xl font-bold text-white mb-2">{selectedImage.title}</h3>
                  <p className="text-zinc-400 mb-2">{selectedImage.description}</p>
                  <p className="text-sm text-zinc-600">
                    {new Date(selectedImage.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Gallery;
